import { hasValue, objectArrayFromSameField, objectIntoArray } from 'globalutils/fmUtils';
import { get, isEqual } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { lazy, useState } from 'react';
import { schema } from './schema';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));
const Timezone = lazy(() => import(`@/modules/Timezone/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`).default;
const ErrorIcon = lazy(() => import(`globalcomponents/ErrorIcon/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));

const CompanyDataForm = ({
  apiStatus,
  history,
  Styled,
  i18n,
  innerComponent,
  companyData,
  states,
  webfleetProviders,
  saveCompanyData,
  callback,
  cancelSubscription,
  allCycleRules,
  allCycleExceptions,
  allVehicleTypes,
  updateCardDetails,
  updateCard,
  eventTypes: eventList,
}) => {
  const eventTypes = objectIntoArray(eventList);
  const [AddEditModalObject, setAddEditModalObject] = useState(null);

  const closeAddEditModal = () => {
    setAddEditModalObject(null);
  };

  const apiId = 'saveCompanyData';
  //Check whether the api is calling
  const hasSaveApiStatus = hasValue(apiStatus.status, 'api', apiId);
  const RenderComponent = lazy(() => import(`@/${innerComponent}`));

  const getTmezonePopup = (type, message, payload) => {
    payload = {
      ...payload,
      data: {
        ...payload.data,
        timeZone: companyData?.companyDto?.timeZoneId,
      },
    };

    if (type === 'error') {
      setAddEditModalObject({
        header: '',
        content: () => {
          return (
            <div className="successpopup">
              <ErrorIcon />
              <h2> {i18n.t(message)} </h2>
              <div className="deletePopupBtn">
                <Button
                  type="submit"
                  label={i18n.t('common.ok')}
                  onClick={() => {
                    closeAddEditModal();
                  }}
                />
              </div>
            </div>
          );
        },
      });
    }
    if (message === 'Time zone not available') {
      setAddEditModalObject({
        header: '',
        content: () => {
          return (
            <Timezone closeModal={closeAddEditModal} callback={callback} payload={payload} saveCompanyData={saveCompanyData} />
          );
        },
      });
    }
  };

  const getData = (fields) => {
    const formattedFields = {
      ...fields,
      otherLocationPhone: fields?.otherLocationPhone.length ? fields.otherLocationPhone : '',
      state: fields.state.value,
      webfleetProvider: fields?.webfleetProvider?.value,
      maintenancePeriod: fields?.maintenancePeriod?.value,
      assignedVehicleType: fields?.assignedVehicleType?.value || null,
      defaultCycleRule: fields?.defaultCycleRule?.value || null,
      assignedCycleExceptions: fields?.assignedCycleExceptions
        ? fields.assignedCycleExceptions.map((key) => {
            return key.value;
          })
        : null,
      assignedCycleRules: fields?.assignedCycleRules
        ? fields.assignedCycleRules.map((key) => {
            return key.value;
          })
        : null,
    };

    if (checkServiceProvider('form_getdata_fields', window.$environment?.SERVICE_PROVIDER, false)) {
      formattedFields.alertsToEmail = isEqual(fields?.alertsToEmail, fields?.defaultAlertsToEmail)
        ? null
        : !get(fields, 'alertsToEmail') && get(fields, 'defaultAlertsToEmail.length') > 0
        ? []
        : fields?.alertsToEmail?.map((e) => e?.value);
    } else {
      delete formattedFields['alertsToEmail'];
    }

    ['timeZone', 'defaultAlertsToEmail', 'contractExpirationDate', 'currentSubscriptionCost'].forEach(
      (key) => delete formattedFields[key]
    );

    return formattedFields;
  };

  const handleSubmit = (fields) => {
    saveCompanyData({
      apiId,
      data: getData(fields),
      callback: getTmezonePopup,
    });
  };

  return (
    <>
      <Form
        type="server"
        direction="column"
        initialValues={{
          subscriptionTier: companyData?.companyDto?.subscriptionTier || '',
          company: companyData?.companyDto?.company || '',
          dotNumber: companyData?.companyDto?.dotNumber || '',
          maintenancePeriod: companyData?.companyDto?.maintenancePeriod
            ? {
                label: companyData?.companyDto?.maintenancePeriod,
                value: companyData?.companyDto?.maintenancePeriod,
              }
            : '',
          maintenanceMileage: companyData?.companyDto?.maintenanceMileage || '',
          address: companyData?.companyDto?.address || '',
          city: companyData?.companyDto?.city || '',
          state: companyData?.companyDto?.state
            ? {
                label: companyData?.companyDto?.state,
                value: companyData?.companyDto?.state,
              }
            : '',
          webfleetProvider: companyData?.companyDto?.webfleetProvider
            ? {
                label: companyData?.companyDto?.webfleetProvider,
                value: companyData?.companyDto?.webfleetProvider,
              }
            : '',
          zipCode: companyData?.companyDto?.zipCode || '',
          email: companyData?.email || '',
          companyPhone: companyData?.companyDto?.companyPhone || '',
          initialTrucks: companyData?.companyDto?.initialTrucks || 0,
          timeZone: companyData?.companyDto?.timeZone || '',
          contactPerson: companyData?.companyDto?.contactPerson || '',
          otherLocationContactPerson: companyData?.companyDto?.otherLocationContactPerson || '',
          otherLocationAddress: companyData?.companyDto?.otherLocationAddress || '',
          otherLocationPhone: companyData?.companyDto?.otherLocationPhone || '',
          website: companyData?.companyDto?.website || '',
          skipVehicleFilter: companyData?.companyDto?.skipVehicleFilter || false,
          assignedVehicleType:
            companyData?.assignedVehicleType !== null
              ? {
                  label: companyData?.assignedVehicleType,
                  value: companyData?.assignedVehicleType,
                }
              : allVehicleTypes[0],
          assignedCycleRules:
            companyData?.assignedCycleRules !== null
              ? companyData?.assignedCycleRules && objectArrayFromSameField(companyData?.assignedCycleRules)
              : '',
          assignedCycleExceptions:
            companyData?.assignedCycleExceptions !== null
              ? companyData?.assignedCycleExceptions && objectArrayFromSameField(companyData?.assignedCycleExceptions)
              : '',
          defaultCycleRule: companyData?.defaultCycleRule
            ? {
                label: companyData?.defaultCycleRule,
                value: companyData?.defaultCycleRule,
              }
            : '',
          showOw: companyData?.companyDto?.showOw || false,
          contractExpirationDate: moment(companyData?.companyDto?.contractExpirationDate).format('L'),
          currentSubscriptionCost: companyData?.companyDto?.currentSubscriptionCost
            ? `$${(companyData?.companyDto?.currentSubscriptionCost / 100).toFixed(2)}`
            : '$0.00',
          isIftaEnabled: companyData?.companyDto?.isIftaEnabled,
          isDvirEnabled: companyData?.companyDto?.isDvirEnabled,
          clutchSubscriptionPlan: companyData?.companyDto?.clutchSubscriptionPlan,
          alertsToEmail: companyData?.companyDto?.alertsToEmail?.length
            ? companyData?.companyDto?.alertsToEmail?.map((e) => ({ label: eventList ? eventList[e] : e, value: e }))
            : [],
          defaultAlertsToEmail: companyData?.companyDto?.alertsToEmail?.length
            ? companyData?.companyDto?.alertsToEmail?.map((e) => ({ label: eventList ? eventList[e] : e, value: e }))
            : [],
        }}
        RenderComponent={RenderComponent}
        handleOnSubmit={handleSubmit}
        customProps={{
          hasSaveApiStatus,
          history,
          Styled,
          i18n,
          states,
          webfleetProviders,
          cancelSubscription,
          apiStatus,
          callback,
          allCycleRules,
          allCycleExceptions,
          allVehicleTypes,
          updateCard,
          updateCardDetails,
          eventTypes,
        }}
        validationSchema={schema}
      />
      {AddEditModalObject && (
        <Modal
          open={!!AddEditModalObject}
          setOpen={closeAddEditModal}
          data={AddEditModalObject}
          background="black"
          timeZone
          isSuccess
        />
      )}
    </>
  );
};
export default CompanyDataForm;

CompanyDataForm.defaultType = {
  hasRegistration: true,
};
CompanyDataForm.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  fetchLogin: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  Styled: PropTypes.object.isRequired,
  i18n: PropTypes.array.isRequired,
  hasRegistration: PropTypes.bool,
};
